import { inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';
import { API_ERROR_REASON, API_ERROR_TYPE } from '../enums';

@Injectable()
export class LoginInterceptor implements HttpInterceptor {
  private isRefreshing: boolean = false;
  private accessToken$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private isSigningIn = false;
  private signIn$ = new BehaviorSubject<boolean | null>(null);

  private readonly tokenExcludedPaths: string[] = [
    '/auth/token/refresh',
    '/auth/sign-in/password',
    '/auth/sign-in/google',
    '/auth/password/reset/request',
    '/auth/password/reset/submit',
    '/auth/account/link/google',
    '/users/sign-up',
    '/users/sign-up/google',
    '/users/verify-email/request',
    '/users/verify-email/submit'
  ];

  private readonly errorMessageExcluded: string[] = [
    API_ERROR_TYPE.PERMISSION_DENIED,
    API_ERROR_REASON.EMAIL_LINK_REQUIRED,
    API_ERROR_REASON.EMPLOYEE_COMPANY_MISMATCH
  ];

  constructor(
    private readonly toastrService: ToastrService,
    private readonly injector: Injector
  ) {}

  get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isTokenUrlExcluded(request.url)) {
      request = this.addTokenHeader(request, this.authService.accessToken);
    }

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body != null && !event.body.success) {
          const errorType = event.body.error?.type;
          const errorReason = event.body.error?.reason;
          if (errorType) {
            if (errorType === API_ERROR_TYPE.PERMISSION_DENIED && this.authService.getCurrentCompanyId()) {
              this.handlePermissionDenied();
            }
            if (!this.isErrorMessageExcluded(errorType) && !this.isErrorMessageExcluded(errorReason)) {
              this.toastrService.error(event.body.error.description);
            }
          }
        }
      }),
      catchError((error) => this.handleError(error, request, next))
    );
  }

  private isTokenUrlExcluded(url: string): boolean {
    if (!url.startsWith(environment.baseUrl)) {
      return true;
    }

    return this.tokenExcludedPaths.some((path) => url.startsWith(environment.baseUrl + path));
  }

  private isErrorMessageExcluded(type: string): boolean {
    return this.errorMessageExcluded.some((value) => value == type);
  }

  private handleError(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      if (request.url.includes(environment.baseUrl + '/auth/token/refresh')) {
        this.authService.logout();
        return throwError(() => error);
      }
      return this.handle401Error(request, next);
    }
    this.toastrService.error(error.message);
    return throwError(() => error);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessToken$.next(null);

      const refreshToken = this.authService.refreshToken;
      if (refreshToken) {
        return this.authService.refreshTokenPair().pipe(
          switchMap((response) => {
            this.isRefreshing = false;
            this.authService.accessToken = response.data!.token;
            this.authService.refreshToken = response.data!.refreshToken;
            this.accessToken$.next(response.data!.token);

            return next.handle(this.addTokenHeader(request, response.data!.token));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.authService.logout();
            return throwError(() => err);
          })
        );
      } else {
        this.authService.logout();
      }
    }

    return this.accessToken$.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token!)))
    );
  }

  private handlePermissionDenied(): void {
    const companyId = this.authService.getCurrentCompanyId();

    if (!this.isSigningIn && companyId) {
      this.isSigningIn = true;
      this.signIn$.next(null);

      this.authService.employeeSignIn(companyId).subscribe({
        next: () => {
          this.isSigningIn = false;
          this.signIn$.next(true);
        },
        error: () => {
          this.isSigningIn = false;
          this.signIn$.next(false);
        }
      });
    }
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + token)
    });
  }
}
